import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Chip, Grid, List, ListItemButton, ListItemText, Paper, Stack, Typography } from '@mui/material';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoffOutlined';
import { getJourney, getReadyToFlyDashboard } from '../../api/readyToFly';
import { EmptyState } from '../../components/EmptyState';

const StatBlock = ({ label, value }: { label: string; value: number }) => (
  <Box>
    <Typography variant="h6" sx={{ fontWeight: 700 }}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

export const ReadyToFlyCard = () => {
  const navigate = useNavigate();
  const statsQuery = useQuery({
    queryKey: ['dashboard', 'ready-to-fly-stats'],
    queryFn: getReadyToFlyDashboard,
  });
  const departuresQuery = useQuery({
    queryKey: ['dashboard', 'upcoming-departures'],
    queryFn: () => getJourney({ page: 1, pageSize: 5, status: 'READY_TO_FLY' }),
  });

  const departures = (departuresQuery.data?.data ?? []).filter((a) => a.departureDate);

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FlightTakeoffIcon color="warning" fontSize="small" />
          <Typography variant="subtitle1">Ready to fly</Typography>
        </Stack>
        <Typography
          variant="caption"
          color="primary"
          sx={{ cursor: 'pointer', fontWeight: 600 }}
          onClick={() => navigate('/ready-to-fly')}
        >
          View all
        </Typography>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={3}>
          <StatBlock label="Ready" value={statsQuery.data?.studentsReady ?? 0} />
        </Grid>
        <Grid item xs={3}>
          <StatBlock label="Flying today" value={statsQuery.data?.flyingToday ?? 0} />
        </Grid>
        <Grid item xs={3}>
          <StatBlock label="This week" value={statsQuery.data?.flyingThisWeek ?? 0} />
        </Grid>
        <Grid item xs={3}>
          <StatBlock label="Without batch" value={statsQuery.data?.studentsWithoutBatch ?? 0} />
        </Grid>
      </Grid>

      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
        Upcoming departures
      </Typography>
      {departures.length === 0 ? (
        <EmptyState loading={departuresQuery.isLoading} message="No upcoming departures scheduled" />
      ) : (
        <List dense disablePadding>
          {departures.map((app) => (
            <ListItemButton
              key={app.id}
              onClick={() => navigate('/ready-to-fly')}
              sx={{ borderRadius: 2, px: 1 }}
            >
              <ListItemText
                primary={`${app.student.firstName} ${app.student.lastName}`}
                secondary={app.university.name}
              />
              <Chip
                size="small"
                variant="outlined"
                label={app.departureDate ? new Date(app.departureDate).toLocaleDateString() : '—'}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Paper>
  );
};
